/**
 * Domain enums shared by server and web.
 * Each is declared once as a const tuple so Zod, TypeScript and Postgres agree.
 */
/**
 * FOUR roles since 2026-09-17, not three.
 *
 * Nemer asked for an HR person who handles staff records, leave and
 * timesheets and sees nothing else — no clients, no deals, no invoices.
 * The screen axis could not express that: it NARROWS where a role applies
 * and never grants, so an HR person had to hold some other role with all
 * its permissions underneath, and anyone who could edit their screen list
 * could hand them the whole system back.
 *
 * This is a deliberate change to a golden rule, recorded in DECISIONS.md
 * §13. It is NOT licence for role sprawl: Consultant, Auditor, Trainer and
 * Viewer remain refused, because each of those is a screen list or an
 * ownership filter rather than a different set of things a person may do.
 */
export const USER_ROLES = ['admin', 'accounting', 'sales', 'hr'];
export const USER_STATUSES = ['active', 'suspended'];
export const LANGUAGES = ['en', 'ar'];
/** MEFOSA bills in these four. Amounts are always integer minor units. */
export const CURRENCIES = ['USD', 'LBP', 'AED', 'EUR'];
/** Minor units per major unit. LBP has no subunit in practice. */
export const CURRENCY_MINOR_UNITS = {
    USD: 100,
    EUR: 100,
    AED: 100,
    LBP: 1,
};
export const OFFICES = ['beirut', 'dubai'];
/** Where a company sits in its relationship with MEFOSA. */
export const COMPANY_LIFECYCLES = ['lead', 'prospect', 'client', 'dormant', 'lost'];
export const COMPANY_TYPES = [
    'manufacturer', 'restaurant', 'hotel', 'catering', 'retail', 'importer',
    'exporter', 'farm', 'warehouse', 'bakery', 'dairy', 'meat_poultry',
    'beverage', 'central_kitchen', 'laboratory', 'university', 'ngo',
    'government', 'donor', 'supplier', 'other',
];
/**
 * MEFOSA's own five pipeline stages, taken from their CRM workbook:
 * 1 Lead, 2 Follow up/quote sent, 3 Order Confirmed/Proforma sent,
 * 4 Closed/Partial pay, 5 Closed/Full pay.
 *
 * This IS money-first — closing records revenue and cost. That was a
 * deliberate revision on 2026-08-31 replacing the earlier Initiative and its
 * level 1-4 ladder; docs/DECISIONS.md §6 records what it costs, namely the
 * value-less rows (MoUs, JVs) the old model carried.
 */
export const DEAL_STAGES = [1, 2, 3, 4, 5];
/** Which of the three sheets a stage belongs on. Derived, never stored. */
export function stageBoard(stage) {
    if (stage <= 2)
        return 'follow_up';
    if (stage === 3)
        return 'implementation';
    return 'closed';
}
/** A deal is closed once it reaches partial or full payment. */
export function isClosedStage(stage) {
    return stage >= 4;
}
/** Their "Type" column. BID is written that way in their sheets. */
/**
 * Service, Sales or BID — Nemer's call, 2026-09-09.
 *
 * Replaced 'tender', which no deal had ever been given: 43 were Service and 8
 * BID. Renamed rather than added-and-dropped so the pg enum keeps its other
 * two values and those 8 BIDs keep their classification.
 *
 * Not to be confused with CLIENT_TYPES below, which has a 'tender' of its own
 * and is a different question — how MEFOSA reaches a client, not what the job
 * is. See docs/DECISIONS.md §6.
 */
export const DEAL_TYPES = ['service', 'sales', 'bid'];
/**
 * Their "State" column, which is colour-coded in the workbook rather than
 * written out. Kept as a small named set so the colour means something a
 * report can group by.
 */
export const DEAL_STATES = ['on_track', 'waiting', 'at_risk', 'not_awarded', 'cancelled'];
/** From the consolidated customer index. */
export const CLIENT_TYPES = ['direct', 'tender', 'ngo', 'distributor', 'partner', 'other'];
export const LEAD_SOURCES = [
    'website', 'referral', 'linkedin', 'conference', 'cold_outreach',
    'existing_client', 'certificate_renewal', 'partner', 'field_visit', 'other',
];
export const FOLLOW_UP_CHANNELS = [
    'call', 'email', 'whatsapp', 'meeting', 'field_visit', 'note',
];
export const FOLLOW_UP_OUTCOMES = [
    'reached', 'no_answer', 'callback_requested', 'not_interested',
    'interested', 'quote_requested', 'meeting_set', 'other',
];
export const CONTACT_ROLES = [
    'decision_maker', 'influencer', 'technical', 'finance', 'operations', 'other',
];
export const AUDIT_ACTIONS = ['create', 'update', 'delete', 'login', 'logout', 'import'];
export const IMPORT_STATUSES = ['staged', 'mapped', 'committed', 'cancelled', 'failed'];
/** What a spreadsheet is being imported as. */
export const IMPORT_ENTITIES = ['company', 'contact', 'deal'];
// =====================================================================
//  Food-safety domain
// =====================================================================
/** The standards MEFOSA implements, audits and trains against. */
export const STANDARDS = [
    'HACCP', 'ISO 22000', 'FSSC 22000', 'BRCGS', 'IFS', 'SQF',
    'ISO 9001', 'ISO 14001', 'ISO 45001', 'GMP 21 CFR 117',
    'Halal', 'Organic', 'GlobalG.A.P.', 'TACCP/VACCP', 'CODEX', 'Other',
];
export const AUDIT_TYPES = [
    'gap_analysis', 'internal', 'pre_certification', 'certification',
    'surveillance', 'supplier', 'unannounced', 'follow_up',
];
export const AUDIT_STATUSES = [
    'scheduled', 'in_progress', 'report_pending', 'completed', 'cancelled',
];
export const AUDIT_RESULTS = ['pass', 'pass_with_conditions', 'fail'];
/**
 * Roles on an audit team, in ISO 19011 terms.
 *
 * The lead auditor is the one who signs the report; a technical expert brings
 * process knowledge but does not audit alone; an auditor in training is
 * observed. MEFOSA sends two or three people on a real audit, so recording
 * only the lead loses who actually did the work.
 */
export const AUDIT_TEAM_ROLES = [
    'lead_auditor', 'auditor', 'technical_expert', 'auditor_in_training',
];
/** GFSI-style non-conformity grading. */
export const FINDING_SEVERITIES = ['critical', 'major', 'minor', 'observation'];
export const FINDING_STATUSES = [
    'open', 'in_progress', 'submitted', 'verified', 'closed',
];
/**
 * CAPA response windows by severity, in days. A critical non-conformity
 * demands near-immediate correction under every GFSI scheme.
 */
export const CAPA_DAYS = {
    critical: 7,
    major: 30,
    minor: 60,
    observation: 90,
};
/** Derived from the expiry date - never stored. */
export const CERTIFICATE_STATUSES = [
    'valid', 'expiring_soon', 'expired', 'suspended', 'withdrawn',
];
/** Days before expiry at which a certificate becomes a renewal opportunity. */
export const RENEWAL_WINDOW_DAYS = 90;
// =====================================================================
//  Academy
// =====================================================================
export const COURSE_LEVELS = [
    'awareness', 'foundation', 'intermediate', 'advanced', 'lead_implementer', 'lead_auditor',
];
/**
 * MEFOSA's own monthly cycle, from their training brochures: a basic course on
 * the first Wednesday, technical on the second, sectorial on the third and
 * technological on the fourth.
 */
export const COURSE_TRACKS = ['basic', 'technical', 'sectorial', 'technological'];
export const DELIVERY_MODES = ['in_person', 'online_live', 'self_paced', 'hybrid'];
export const SESSION_STATUSES = [
    'planned', 'open', 'full', 'running', 'completed', 'cancelled',
];
export const ENROLMENT_STATUSES = [
    'registered', 'confirmed', 'attended', 'no_show', 'cancelled',
];
// =====================================================================
//  Laboratory
// =====================================================================
export const ANALYSIS_TYPES = [
    'microbiological', 'chemical', 'pesticide_residue', 'heavy_metals', 'allergen',
    'nutritional', 'shelf_life', 'food_fraud', 'water_quality', 'organoleptic',
    'water_activity', 'swab_environmental',
];
export const SAMPLE_STATUSES = [
    'received', 'in_analysis', 'completed', 'rejected',
];
export const LAB_VERDICTS = ['pass', 'fail', 'inconclusive'];
// =====================================================================
//  Invoicing
// =====================================================================
export const QUOTATION_STATUSES = [
    'draft', 'sent', 'accepted', 'declined', 'expired',
];
export const INVOICE_STATUSES = [
    'draft', 'issued', 'part_paid', 'paid', 'cancelled',
];
/**
 * The statuses a PERSON may choose. The other two are not theirs to choose.
 *
 * `part_paid` and `paid` are absent on purpose: both are derived from the
 * payments recorded against the invoice (`settlementStatus`), so picking one
 * here set a column nobody reads and the invoice carried on showing what it was
 * actually owed. That is how an invoice came to sit in the list labelled "Paid"
 * while its own detail screen said no payment had been recorded.
 *
 * To mark an invoice paid, record the payment. The status follows.
 *
 * INVOICE_STATUSES stays the full set: it is the database enum, and the list
 * filter legitimately offers all five because derived values are filterable.
 */
export const INVOICE_WORKFLOW_STATUSES = ['draft', 'issued', 'cancelled'];
/**
 * Every status an invoice can be SHOWN with — the database enum plus
 * `credited`, which exists only on read (DECISIONS.md §16).
 *
 * `credited` means the invoice had a total and live credit notes have brought
 * what it is worth down to nothing. It is derived from the credit notes, never
 * stored, and deliberately NOT added to the `invoice_status` database enum:
 * Postgres cannot drop an enum value, and a stored "credited" would be a second
 * answer that drifts the moment a credit note is removed.
 *
 * Use this for list filters and status chips; INVOICE_STATUSES stays the
 * database enum and INVOICE_WORKFLOW_STATUSES what a person may choose.
 */
export const INVOICE_DISPLAY_STATUSES = [...INVOICE_STATUSES, 'credited'];
/**
 * What a credit note reads as, derived from the refunds against it and from
 * what its invoice actually owes back (DECISIONS.md §16). Display only — a
 * credit note has no stored status, and a soft delete is its only "void".
 *
 *   applied        absorbed by an unpaid balance; nothing to pay back
 *   refund_due     money is owed back to the client and none has gone yet
 *   part_refunded  some has gone back, more is still owed
 *   refunded       everything owed back on it has been paid
 */
export const CREDIT_NOTE_STATUSES = ['applied', 'refund_due', 'part_refunded', 'refunded'];
export const PAYMENT_METHODS = ['cash', 'cheque', 'bank_transfer', 'card', 'other'];
// =====================================================================
//  Trading / inventory
// =====================================================================
export const STOCK_MOVEMENT_KINDS = ['in', 'out', 'adjustment'];
// =====================================================================
//  Donor projects
// =====================================================================
export const PROJECT_STATUSES = [
    'pipeline', 'active', 'on_hold', 'completed', 'closed',
];
export const MILESTONE_STATUSES = ['pending', 'in_progress', 'done', 'missed'];
/**
 * The sidebar sections, and the unit an administrator grants access by.
 *
 * Text, not a Postgres enum: these are a UI grouping that will grow (a
 * Settings section, an HR section), and Postgres cannot drop an enum value.
 */
export const APP_MODULES = [
    'overview', 'pipeline', 'compliance', 'academy',
    'operations', 'billing', 'people', 'administration',
];
/**
 * The individual screens an administrator can grant, within a section.
 *
 * Derived from the routes rather than the sidebar: several screens have detail
 * pages that appear in no menu (/companies/:id, /deals/:id …), and a grant
 * that covered the list but not the detail would 403 on a link from a screen
 * the person can plainly see.
 */
export const APP_ENTITIES = [
    // overview
    'dashboard', 'today', 'activity-log',
    // pipeline
    'companies', 'contacts', 'whatsapp', 'deals', 'import', 'duplicates',
    // compliance
    'audits', 'findings', 'certificates',
    // academy
    'courses', 'sessions', 'trainees',
    // operations
    'laboratory', 'inventory', 'purchases', 'suppliers', 'projects',
    // billing
    'quotations', 'invoices', 'creditNotes', 'expenses', 'payables',
    // people
    'staff', 'leave', 'timesheets',
    // administration
    'reports', 'team',
];
// =====================================================================
//  Purchasing
// =====================================================================
/**
 * What a PERSON has decided about an order.
 *
 * Deliberately does NOT include "received" or "part received": whether stock
 * has arrived is derived from the batches booked against the order's lines
 * (rule 11). Storing it beside the batches that prove it is two sources of
 * truth for one fact, and they drift the first time a delivery is corrected.
 */
export const PURCHASE_ORDER_STATUSES = ['draft', 'ordered', 'cancelled'];
/** How much of an order has actually turned up. Computed, never stored. */
export const RECEIPT_STATES = ['none', 'partial', 'complete', 'over'];
/**
 * The three things a person does to a record.
 *
 * Vocabulary, so it sits here rather than in permissions.ts — the user
 * contract in schemas/auth.ts needs it and must not import the permission
 * matrix to get it.
 */
export const ENTITY_ACTIONS = ['add', 'edit', 'delete'];
// =====================================================================
//  HR — staff records, leave and timesheets (DECISIONS.md §13)
// =====================================================================
/**
 * How somebody is engaged, which decides what they accrue.
 *
 * A consultant on a day rate earns no annual leave under Lebanese labour law,
 * so this is not cosmetic — `leaveEntitlementDays` is only meaningful for the
 * employed types, and the screen says so rather than showing a balance that
 * means nothing.
 */
export const CONTRACT_TYPES = [
    'full_time', 'part_time', 'consultant', 'intern',
];
/**
 * Kinds of leave MEFOSA records.
 *
 * Lebanese labour law grants 15 working days annual leave after one year of
 * service, sick leave on a sliding scale by seniority, and 10 weeks maternity.
 * Only ANNUAL is deducted from the entitlement here — the rest are recorded so
 * the calendar is honest about who is absent, without pretending this system
 * computes a statutory sick-pay ladder it has no basis for.
 */
export const LEAVE_TYPES = [
    'annual', 'permission', 'sick', 'unpaid', 'maternity', 'paternity', 'compassionate', 'other',
];
/** Only annual leave comes off the yearly entitlement. */
/**
 * Annual leave and permissions come off the yearly entitlement.
 *
 * A permission is an hour or two off — an appointment, leaving early — and
 * Nemer chose for it to be charged against the annual balance as part of a
 * day. Sick leave has its OWN allowance under Art. 40 and is deliberately
 * not deducted here; merging the two would understate what somebody is
 * owed when they are ill. See DECISIONS.md §13.
 */
export function deductsFromEntitlement(type) {
    return type === 'annual' || type === 'permission';
}
/** Recorded in HOURS rather than days, because that is what it is. */
export function isHourly(type) {
    return type === 'permission';
}
export const LEAVE_STATUSES = ['pending', 'approved', 'rejected', 'cancelled'];
/** Only an approved request is actually time off. */
export function countsAsTaken(status) {
    return status === 'approved';
}
/**
 * What an hour on a timesheet was spent on.
 *
 * Billable vs not is the distinction that makes a timesheet worth filling in;
 * everything else is detail. Kept small deliberately.
 */
export const WORK_KINDS = [
    'audit', 'training', 'consultancy', 'lab', 'project', 'admin', 'travel', 'other',
];
// =====================================================================
//  Accounting — money going OUT (DECISIONS.md §14)
// =====================================================================
/**
 * What an expense was for.
 *
 * Chosen for a consultancy that travels to client sites, not from a textbook
 * chart of accounts: MEFOSA's books live with their accountant (§2), and this
 * list exists so a report can answer "what did that audit actually cost us".
 */
export const EXPENSE_CATEGORIES = [
    'travel', 'per_diem', 'accommodation', 'fuel', 'office', 'utilities',
    'subscriptions', 'professional_fees', 'training', 'equipment',
    'entertainment', 'bank_charges', 'other',
];
/**
 * Where an out-of-pocket expense has got to.
 *
 * `not_claimed` is the common case and the default: MEFOSA paid the supplier
 * directly and nobody is owed anything. The rest is the claim trail for money
 * a colleague spent themselves.
 */
export const CLAIM_STATUSES = [
    'not_claimed', 'submitted', 'approved', 'rejected', 'reimbursed',
];
/** Money a colleague is actually owed back, as opposed to merely claimed. */
export function isOwedBack(status) {
    return status === 'approved';
}
/** A claim that has been settled, so it stops counting as owed. */
export function isSettled(status) {
    return status === 'reimbursed' || status === 'rejected';
}
/**
 * Marital status, which HR records because the NSSF needs it.
 *
 * Not idle demography: Lebanese family allowances are paid through the NSSF
 * for a dependent spouse and dependent children, so this and `dependents` are
 * what an employer's declaration turns on. The AMOUNTS are not computed here —
 * that is payroll, which lives with MEFOSA's accountant (§13, §2).
 */
export const MARITAL_STATUSES = ['single', 'married', 'divorced', 'widowed'];
//# sourceMappingURL=enums.js.map